/**
 * Reset Buttons Component
 * Provides "Reset to Saved" and "Reset to Default" functionality
 */

import React from 'react';
import { Button } from '@/core/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/core/components/ui/dropdown-menu';
import { RotateCcw, ChevronDown } from 'lucide-react';

interface ResetButtonsProps {
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;

  /** Whether currently using default config */
  isDefault: boolean;

  /** Callback to reset to last saved configuration */
  onResetToSaved: () => void;

  /** Callback to reset to default configuration */
  onResetToDefault: () => void;

  /** Optional CSS class */
  className?: string;
}

/**
 * Reset Buttons Component
 * Dropdown menu with reset options
 */
export function ResetButtons({
  hasUnsavedChanges,
  isDefault,
  onResetToSaved,
  onResetToDefault,
  className = '',
}: ResetButtonsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={className}
          disabled={!hasUnsavedChanges && isDefault}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onClick={onResetToSaved}
          disabled={!hasUnsavedChanges}
          className="cursor-pointer"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">Reset to Saved</span>
            <span className="text-xs text-muted-foreground">
              Discard unsaved changes
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={onResetToDefault}
          className="cursor-pointer"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">Reset to Default</span>
            <span className="text-xs text-muted-foreground">
              Restore factory defaults
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
